import { Input } from '@optools/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@optools/components/ui/table';
import { cn } from '@optools/components/ui/utils';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel as getFilteredRowModelFn,
  getPaginationRowModel as getPaginationRowModelFn,
  getSortedRowModel as getSortedRowModelFn,
  RowSelectionState,
  SortingState,
  TableOptions,
  useReactTable,
} from '@tanstack/react-table';
import * as React from 'react';

import { DataTablePagination } from './data-table-pagination';
import { DataTableViewOptions } from './data-table-view-options';

export type DataTableProps<TData> = {
  children?: React.ReactNode;
  className?: string;
  /** Optional row click Handler */
  handleRowClick?: (rowIndex: number) => void;
  /** Shows/hides pagination */
  pagination?: boolean;
  /** Optional selected row  */
  rowSelection?: RowSelectionState;
  /** Shows/hides search input */
  search?: boolean;
  /** Shows/hides view options menu */
  viewOptions?: boolean;
} & Omit<TableOptions<TData>, 'getCoreRowModel'>;

export function DataTable<TData>({
  children,
  className,
  pagination = true,
  search = true,
  viewOptions = true,
  ...props
}: DataTableProps<TData>) {
  const {
    enableRowSelection,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    handleRowClick,
    initialState,
    onGlobalFilterChange,
    onSortingChange,
    rowSelection = {},
    state,
    ...restOfOptions
  } = props;

  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = React.useState<string | undefined>();

  const table = useReactTable({
    enableRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel ?? getFilteredRowModelFn(),
    getPaginationRowModel: pagination
      ? (getPaginationRowModel ?? getPaginationRowModelFn())
      : undefined,
    getSortedRowModel: getSortedRowModel ?? getSortedRowModelFn(),
    initialState: { ...(pagination && { pagination: { pageSize: 25 } }), ...initialState },
    onGlobalFilterChange: onGlobalFilterChange ?? setGlobalFilter,
    onSortingChange: onSortingChange ?? setSorting,
    state: { globalFilter, rowSelection, sorting, ...state },
    ...restOfOptions,
  });

  return (
    <div className={cn('grid h-full w-full content-start space-y-4 overflow-auto p-1', className)}>
      <div className="flex items-center gap-2">
        {children}
        <div className="flex flex-1 items-center justify-end gap-2">
          {search && (
            <Input
              className="w-full max-w-80"
              placeholder="Search"
              role="searchbox"
              onChange={(e) => setGlobalFilter(e.target.value)}
            />
          )}
          {viewOptions && <DataTableViewOptions table={table} />}
        </div>
      </div>

      <div className="flex-1 overflow-hidden rounded-md border">
        <Table containerClassName="h-full relative">
          <TableHeader className="sticky top-0 z-10 border-b bg-background">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      key={header.id}
                      className={cn('px-4', header.column.columnDef.meta?.headerClassName)}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  {...(enableRowSelection &&
                    handleRowClick && {
                      onClick: () => handleRowClick(row.index),
                    })}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={cn(
                        'py-2 pr-2 pl-4 whitespace-nowrap',
                        cell.column.columnDef.meta?.cellClassName,
                      )}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell className="h-24 text-center" colSpan={table.getAllColumns().length}>
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {pagination && <DataTablePagination table={table} />}
    </div>
  );
}
